import React, { FC, useState } from 'react';

import { Button, Spinner, Text } from '@components/common';
import { Portal } from '@components/Portal';
import { Enums, getBlockLabel, Models } from '@components/SurveyBuilder';
import { TaskHeader } from '@components/Unmoderated/components/TaskHeader';
import { TaskLoadedBlocker } from '@components/Unmoderated/components/TaskLoadedBlocker';

import { useUnmoderatedContext } from '../hooks/useUnmoderatedContext';

import { ResponsiveCard } from './ResponsiveCard';

interface Props {
  block: Models.Block<Enums.Kind.websiteTest>;
  iframePortalId: string;
  onSubmit: () => Promise<void>;
  setLoadingModalOpen: (open: boolean) => void;
  setInteractionModalOpen: (open: boolean) => void;
}

export const WebsiteTestTask: FC<Props> = ({
  block,
  iframePortalId,
  onSubmit,
  setLoadingModalOpen,
  setInteractionModalOpen
}) => {
  const { blocks, topBarUnmoderatedLayout, deviceType } = useUnmoderatedContext();

  const [isMinimized, setIsMinimized] = useState(false);
  const [blocker, setBlocker] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [taskInProgress, setTaskInProgress] = useState<boolean>(false);

  const isIframable = block.blockable.settings?.open_in_new_tab ? false : block.blockable.is_iframable;

  const openWebsiteInNewTab = () => {
    setTaskInProgress(true);

    let { url } = block.blockable;

    if (url) {
      if (!/^https?:\/\//i.test(url)) {
        url = `https://${url}`;
      }

      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleOnEndTask = () => {
    if (isLoading && isIframable) {
      setLoadingModalOpen(true);
    } else if (!taskInProgress) {
      setInteractionModalOpen(true);
    } else {
      onSubmit?.();
    }
  };

  const footerRenderer = () => {
    return (
      <div className='flex space-x-2'>
        <Button
          aria-label='Next task'
          className={taskInProgress ? 'btn-custom-brand' : 'btn-custom-brand-secondary'}
          type='button'
          onClick={handleOnEndTask}
          iconSuffix='arrowRight'
          small
          noStyle
        >
          Next task
        </Button>
      </div>
    );
  };

  if (deviceType === 'desktop' && topBarUnmoderatedLayout) {
    return (
      <div className='flex h-screen w-full flex-col overflow-hidden bg-white pt-4'>
        <TaskHeader
          isMinimized={isMinimized}
          setIsMinimized={setIsMinimized}
          block={block}
          renderOptions={footerRenderer}
        />
        <div className='relative flex w-full flex-1 overflow-hidden bg-gray-50'>
          <div className='flex w-full flex-1 flex-col items-center overflow-auto'>
            {isIframable ? (
              <iframe
                src={block.blockable.url ?? ''}
                onLoad={() => {
                  setIsLoading(false);
                  setBlocker(true);
                }}
                title='Website task website'
                className='h-full w-full'
              />
            ) : (
              <div className='w-1/2 max-w-3xl px-4 py-6 text-left'>
                <div className='rounded-lg border border-gray-200 bg-white p-6'>
                  <Text h='600' className='mb-2' bold>
                    Website must be opened in a new tab
                  </Text>
                  <Text h='400' className='mb-6'>
                    Due to security policies on this website URL, it cannot be displayed together with the instructions
                    on the same window. Please follow the link below to open in a new tab, and ensure your entire screen
                    is recording it.
                  </Text>
                  <Button
                    className='btn-custom-brand'
                    type='button'
                    icon='externalLink'
                    onClick={openWebsiteInNewTab}
                    small
                    noStyle
                    fullWidth
                  >
                    Open website
                  </Button>
                </div>
              </div>
            )}
          </div>
          {isIframable && isLoading && (
            <div className='absolute inset-0 flex items-center justify-center bg-white'>
              <Spinner className='h-10 w-10' />
            </div>
          )}

          {blocker && (
            <TaskLoadedBlocker
              blockKind={block.kind}
              onBeginTask={() => {
                setTaskInProgress(true);
                setBlocker(false);
                setIsMinimized(true);
              }}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <ResponsiveCard
      blockKind={getBlockLabel(block.kind)}
      blockPosition={block.position}
      description={block.description}
      title={block.title}
      totalBlocks={blocks.length}
      renderFooter={() => {
        return (
          <div className='flex items-center space-x-2'>
            {!isIframable && (
              <Button
                aria-label='Open website'
                type='button'
                icon='externalLink'
                onClick={openWebsiteInNewTab}
                small
                noStyle
              >
                Open website
              </Button>
            )}

            <Button
              aria-label='End task'
              className='btn-custom-brand'
              type='button'
              onClick={onSubmit}
              icon='checkInCircle'
              small
              noStyle
            >
              End task
            </Button>
          </div>
        );
      }}
    >
      {isIframable && (
        <Portal target={document.getElementById(iframePortalId)}>
          <iframe src={block.blockable.url ?? ''} title='Website task website' className='h-full w-full'></iframe>
        </Portal>
      )}
    </ResponsiveCard>
  );
};
